import { useCallback, useEffect, useRef, useState } from 'react'

import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/router'

import { HeaderTopics } from '@hooks/useHeaderTopics'
import { PageMetadata } from '@hooks/usePageMetadata'

export type UseIsGrowthbookReady = () => boolean

export type GetIsGrowthbookReady = (queryClient: QueryClient) => boolean

const getIsGrowthbookReady: GetIsGrowthbookReady = (queryClient) =>
  !!queryClient.getQueryData<boolean>(['growthbook-ready'])

const useIsGrowthbookReady: UseIsGrowthbookReady = () => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()
  const unsubscribeFnRef = useRef<() => void>(undefined)

  const [isGrowthbookReady, setIsGrowthbookReady] = useState<boolean>(() =>
    getIsGrowthbookReady(queryClient),
  )

  const resetGrowthbookReady = useCallback<
    (pageMetadata: PageMetadata, topics: HeaderTopics) => void
  >(() => {
    setIsGrowthbookReady(false)
  }, [])

  useEffect(() => {
    router.events.on('routeChangeStart', resetGrowthbookReady)

    return () => {
      router.events.off('routeChangeStart', resetGrowthbookReady)
    }
  }, [router, resetGrowthbookReady])

  const updateIsGrowthbookReady = useCallback(
    (args: any) => {
      if (
        ['invalidate'].includes(args?.action?.type) &&
        args.query.queryKey[0] === 'growthbook-ready'
      ) {
        setIsGrowthbookReady(getIsGrowthbookReady(queryClient))
      }
    },
    [queryClient],
  )

  useEffect(() => {
    unsubscribeFnRef.current = queryCache.subscribe(updateIsGrowthbookReady)

    setIsGrowthbookReady(getIsGrowthbookReady(queryClient))

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [queryCache, queryClient, updateIsGrowthbookReady])

  return isGrowthbookReady
}

export { getIsGrowthbookReady }

export default useIsGrowthbookReady
